/**
 * Pin a full-screen `position: fixed` overlay to the VISIBLE viewport.
 *
 * On mobile the on-screen keyboard shrinks the *visual* viewport but NOT the
 * *layout* viewport, and iOS additionally PANS the visual viewport (so its
 * `offsetTop` becomes non-zero). A `position: fixed; inset: 0` overlay stays
 * anchored to the layout viewport, so with the keyboard up its lower part sits
 * behind the keyboard and its internal scroll area can't reach the end of the
 * content (only iOS's own focus-scrolling moves it). There is no CSS unit that
 * tracks the visual viewport's position+size, so we read `window.visualViewport`
 * and write explicit `top`/`left`/`width`/`height` (plus `right`/`bottom: auto`
 * so an `inset: 0` overlay isn't over-constrained) onto the overlay while active,
 * keeping it exactly over the visible slice. Its scrollable body can then reach
 * the end of the form with the keyboard shown.
 *
 * Apply this to the FIXED overlay element (not an inner panel) so the whole
 * modal — header, scrollable body, and footer — lands in the visible area.
 *
 * `destroy()` restores the element's PRIOR inline values for every property it
 * touched (usually empty → falls back to the CSS-driven layout), so it is safe
 * on overlays that already carry inline positioning.
 *
 * No-op (returns an inert handle) when `window.visualViewport` is unavailable —
 * desktop and unsupported engines keep their CSS layout untouched.
 *
 * @param {HTMLElement} el - the fixed overlay to pin (e.g. a modal `__overlay`).
 * @param {{ signal?: AbortSignal }} [opts]
 * @returns {{ destroy: () => void }} handle that removes listeners and clears the inline styles.
 */
export function trackVisualViewportHeight(el, opts = {}) {
  const maybeVv = typeof window !== 'undefined' ? window.visualViewport : null;
  if (!maybeVv) {
    return { destroy() {} };
  }
  /** @type {VisualViewport} */
  const vv = maybeVv;

  let destroyed = false;

  // Snapshot the prior inline values so destroy() restores them exactly (rather
  // than blanking properties the caller may have set). Covers all box-edge
  // props we touch.
  const PROPS = /** @type {const} */ (['position', 'top', 'left', 'right', 'bottom', 'width', 'height']);
  /** @type {Record<string, string>} */
  const prior = {};
  for (const p of PROPS) prior[p] = el.style.getPropertyValue(p);

  function apply() {
    if (destroyed) return;
    // Pin the overlay to the visible slice: offsetTop/offsetLeft move with the
    // keyboard pan; width/height are the area not covered by the keyboard/UI.
    // Neutralize right/bottom so a `inset: 0` overlay isn't over-constrained
    // (left+width+right / top+height+bottom would otherwise conflict).
    el.style.position = 'fixed';
    el.style.top = `${vv.offsetTop}px`;
    el.style.left = `${vv.offsetLeft}px`;
    el.style.right = 'auto';
    el.style.bottom = 'auto';
    el.style.width = `${vv.width}px`;
    el.style.height = `${vv.height}px`;
  }

  function clearStyles() {
    // Restore exactly what was there before (usually empty → drops the inline
    // override and falls back to the CSS-driven layout).
    for (const p of PROPS) {
      if (prior[p]) el.style.setProperty(p, prior[p]);
      else el.style.removeProperty(p);
    }
  }

  const signal = opts.signal;
  function onAbort() { destroy(); }

  function destroy() {
    if (destroyed) return;
    destroyed = true;
    vv.removeEventListener('resize', apply);
    vv.removeEventListener('scroll', apply);
    // Drop the abort listener too, so a manual destroy() (signal not aborted)
    // doesn't leak it until the signal is GC'd.
    if (signal) signal.removeEventListener('abort', onAbort);
    clearStyles(); // restore CSS-driven layout (drop the inline overrides)
  }

  vv.addEventListener('resize', apply);
  // `scroll` fires when the keyboard pans the visual viewport without resizing it.
  vv.addEventListener('scroll', apply);
  if (signal) {
    if (signal.aborted) {
      // Caller already aborted — set up nothing, leave the element untouched.
      vv.removeEventListener('resize', apply);
      vv.removeEventListener('scroll', apply);
      return { destroy() {} };
    }
    signal.addEventListener('abort', onAbort);
  }

  apply(); // position immediately so the overlay is correct before any keyboard event

  return { destroy };
}
